import { AxiosResponse } from "axios";
import BaseController from "controller/BaseController";
import { UserModel } from "model/User.model";
import AddUserRequest from "model/api/AddUserRequest.model";
import AddUserResponse from "model/api/AddUserResponse.model";
import ListUserResponse from "model/api/ListUserResponse.model";
import UpdateUserRequest from "model/api/UpdateUserRequest.model";
import UpdateUserResponse from "model/api/UpdateUserResponse";
import UserSpec from "model/api/UserSpec.model";
import UserService from "service/User.service";
import { ActionType, ViewRenderStatus } from "util/CommonConstant";
import { parseStandardAPIErrorMessage } from "util/CommonUtil";

type IState = any;

class UserListViewController extends BaseController {
  private userListState: IState;

  constructor(state?: IState, dispatch?: any) {
    super(dispatch); // Call the constructor of the parent class if needed.
    this.userListState = state ?? null;
    this.dispatch = dispatch;
  }

  getUsers = async (showLoader = true): Promise<ListUserResponse> => {
    try {
      if (showLoader) this.setPageStatus(ViewRenderStatus.LOADING);
      const res: AxiosResponse<ListUserResponse> = await UserService.getUsers();
      //If the API error
      if (res.status !== 200) {
        const errorMessage = res.data.response?.error_message ?? "Something went wrong";
        this.setPageStatus(ViewRenderStatus.ERROR);
        this.setErrorMessage(errorMessage);
        return Promise.reject("Something went wrong");
      }
      //If the API success
      this.setPageStatus(ViewRenderStatus.SUCCESS);
      return res.data;
    } catch (err) {
      //If the API error
      const errorMessage = parseStandardAPIErrorMessage(err) ?? "Something went wrong";
      this.setPageStatus(ViewRenderStatus.ERROR);
      this.setErrorMessage(errorMessage);
      return Promise.reject(err);
    }
  };

  setUsers = (userList: UserModel[]) => {
    this.dispatch({
      type: ActionType.SET_DATA_CHANGE,
      payload: {
        name: "list",
        value: userList,
      },
    });
  };

  getAndSetUsers = async (showLoader?: boolean): Promise<UserModel[]> => {
    const list = await this.getUsers(showLoader);
    const preparedList = list?.specs?.map((user) => {
      return this.convertToUsersModel(user);
    });
    this.setUsers(preparedList ?? []);
    return preparedList ?? [];
  };

  handleEditUser = async (userData: UserModel) => {
    const userInfo = await UserService.getById(userData.uuid ?? "");
    const user = this.convertToUsersModel(userInfo.data.spec);

    this.dispatch({
      type: ActionType.SET_DATA_CHANGE,
      payload: [
        { name: "user", value: user },
        { name: "isUserEditVisible", value: true },
      ],
    });
  };

  handleOpenInviteUserDialog = () => {
    this.dispatch({
      type: ActionType.SET_DATA_CHANGE,
      payload: { name: "isInviteUserDialogVisible", value: true },
    });
  };

  handleCloseInviteUserDialog = () => {
    this.dispatch({
      type: ActionType.SET_DATA_CHANGE,
      payload: { name: "isInviteUserDialogVisible", value: false },
    });
  };

  handleCloseUserInfo = () => {
    this.dispatch({
      type: ActionType.SET_DATA_CHANGE,
      payload: [
        { name: "isUserEditVisible", value: false },
        { name: "user", value: null },
      ],
    });
  };

  handleUserSearch = (searchParam: string) => {
    this.dispatch({
      type: ActionType.SET_DATA_CHANGE,
      payload: { name: "userSearchParam", value: searchParam },
    });
  };

  onCopyUrl = (url: string) => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        this.dispatch({
          type: ActionType.SET_DATA_CHANGE,
          payload: { name: "isLinkCopied", value: true },
        });
        setTimeout(() => {
          this.dispatch({
            type: ActionType.SET_DATA_CHANGE,
            payload: { name: "isLinkCopied", value: false },
          });
        }, 4000);
      })
      .catch(() => {
        console.log(">> Fail to copy");
      });
  };

  convertToUsersModel = (apiResponse: UserSpec) => {
    return UserService.convertAPIResToModel(apiResponse);
  };

  //Create user
  //this can be moved to auth controller
  signUp = async (isAuth0Enabled = false) => {
    try {
      const payload = new AddUserRequest();
      payload.user = {
        first_name: this.userListState.data?.firstName ?? "",
        last_name: this.userListState.data?.lastName ?? "",
        email: this.userListState.data?.email ?? "",
      };
      payload.password = this.userListState.data?.password ?? "";

      const res: AxiosResponse<AddUserResponse> = await UserService.create(payload, isAuth0Enabled);
      //If the API error
      if (res.status !== 200 && res.status !== 201) {
        const errorMessage = res.data?.response?.error_message ?? "Something went wrong";
        this.setErrorMessage(errorMessage);
        this.setPageStatus(ViewRenderStatus.ERROR);
        return Promise.reject("Something went wrong");
      }
      //If the API success
      this.setPageStatus(ViewRenderStatus.SUCCESS);
      return res.data;
    } catch (err) {
      //If the API error
      const errorMessage = parseStandardAPIErrorMessage(err) ?? "Something went wrong";
      this.setErrorMessage(errorMessage);
      this.setPageStatus(ViewRenderStatus.ERROR);
      return Promise.reject(err);
    }
  };

  //Update user info
  updateUser = async (id: string) => {
    try {
      const payload = new UpdateUserRequest();
      payload.user = {
        first_name: this.userListState.data?.user?.firstName ?? "",
        last_name: this.userListState.data?.user?.lastName ?? "",
        email: this.userListState.data?.user?.email ?? "",
      };
      payload.user_uuid = this.userListState.data?.user?.uuid ?? "";
      payload.password = this.userListState.data?.user?.password ?? "";
      payload.new_password = this.userListState.data?.user?.newPassword ?? "";

      const res: AxiosResponse<UpdateUserResponse> = await UserService.update(id, payload);
      //If the API error
      if (res.status !== 200 && res.status !== 201) {
        const errorMessage = res.data?.response?.error_message ?? "Something went wrong";
        this.setErrorMessage(errorMessage);
        this.setPageStatus(ViewRenderStatus.ERROR);
        return Promise.reject("Something went wrong");
      }
      //If the API success
      this.setPageStatus(ViewRenderStatus.SUCCESS);
      return res.data;
    } catch (err) {
      //If the API error
      const errorMessage = parseStandardAPIErrorMessage(err) ?? "Something went wrong";
      this.setErrorMessage(errorMessage);
      this.setPageStatus(ViewRenderStatus.ERROR);
      return Promise.reject(err);
    }
  };

  //Delete a user
  deleteUser = async (id: string) => {
    try {
      const res = await UserService.delete(id);
      if (res.status !== 200) {
        const errorMessage = res.data.response?.error_message ?? "Something went wrong";
        this.setErrorMessage(errorMessage);
        return Promise.reject("Something went wrong");
      }
      this.setErrorMessage("label.text.userList.toast.message");
      return this.getAndSetUsers(false);
    } catch (err) {
      //If the API error
      const errorMessage =
        parseStandardAPIErrorMessage(err) ?? "Cannot perform this action right now!"; //Change this once the API is ready
      this.setErrorMessage("Cannot perform this action right now!");
      return Promise.reject(new Error(errorMessage));
    }
  };
}

export default UserListViewController;
